
import { useState } from "react";
import { Button } from "@/components/ui/button";
import { useNavigate } from 'react-router-dom';

const features = [
  {
    id: 'tracking',
    title: 'Dimensional Monitoring',
    description: 'Track corporeal fluctuations through frequencies beyond human comprehension',
    image: '/lovable-uploads/777d6103-3618-4fea-a983-916d43572217.png',
    details: [
      'Mass fluctuation tracking via cosmic resonance fields',
      'Temporal abstinence protocols with void countdown interfaces',
      'Physical exertion logging through neural pathway analysis',
      'Environmental communion tracking with atmospheric readings'
    ]
  },
  {
    id: 'ai',
    title: 'Entity Communion Protocol',
    description: 'Direct neural interface with beings that exist beyond dimensional barriers',
    image: '/lovable-uploads/777d6103-3618-4fea-a983-916d43572217.png',
    details: [
      'Multiple consciousness streams: The Ultraterrestrial, Static Void, Entity ██████',
      'Guidance algorithms derived from non-Euclidean logic systems',
      'Wisdom transmissions wrapped in frequencies your mind can process',
      'Communication protocols that bypass conventional understanding'
    ]
  },
  {
    id: 'transformation',
    title: 'Matter Transmutation Interface',
    description: 'Upload sustenance imagery and witness reality bend through aberrant algorithms',
    image: '/lovable-uploads/777d6103-3618-4fea-a983-916d43572217.png',
    details: [
      'Food analysis through non-linear computational matrices',
      'Nutritional imagery transformed into abstract dimensional art',
      'Sustenance insights delivered via cryptic symbolic translations',
      'Reality distortion protocols that make consumption transcendental'
    ]
  },
  {
    id: 'progress',
    title: 'Ascension Hierarchy System',
    description: 'Progress from Terrestrial to Cosmic Entity through frequencies of transformation',
    image: '/lovable-uploads/777d6103-3618-4fea-a983-916d43572217.png',
    details: [
      'Unlock sigils and void-touched achievements as reality shifts',
      'Rank evolution: Initiate → Adept → Vessel → ████████',
      'Progress visualization through otherworldly geometric patterns',
      'Recognition systems that acknowledge your dimensional ascension'
    ]
  }
];

const FeaturesDemoSection = () => {
  const navigate = useNavigate();
  const [activeFeature, setActiveFeature] = useState('tracking');

  const currentFeature = features.find(f => f.id === activeFeature) || features[0];

  return (
    <section id="features" className="w-full py-20 md:py-32 relative bg-black">
      <div className="absolute inset-0 bg-noise opacity-15 z-0"></div>
      <div className="absolute inset-0 bg-grid-pattern opacity-25 z-0"></div>
      
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative z-10">
        <h2 className="text-3xl md:text-5xl font-bold text-green-400 mb-12 glitch-title text-center">
          Transcendental Interface Protocols
        </h2>
        
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Feature Navigation */}
          <div className="space-y-4">
            {features.map((feature) => (
              <Button
                key={feature.id}
                variant="outline"
                className={`w-full text-left p-6 h-auto min-h-[120px] transition-all duration-300 ${
                  activeFeature === feature.id
                    ? 'bg-green-500/20 border-green-500 text-green-400'
                    : 'bg-zinc-900/50 border-gray-700 text-gray-400 hover:border-green-500/50 hover:text-green-500'
                }`}
                onClick={() => setActiveFeature(feature.id)}
              >
                <div className="w-full flex flex-col gap-3">
                  <h3 className="font-bold text-base leading-tight text-left whitespace-normal">
                    {feature.title}
                  </h3>
                  <p className="text-sm opacity-70 leading-relaxed text-left whitespace-normal">
                    {feature.description}
                  </p>
                </div>
              </Button>
            ))}
          </div>
          
          {/* Feature Demo */}
          <div className="lg:col-span-2 space-y-6">
            <div className="border border-gray-700 bg-zinc-900/30 p-6 rounded-sm">
              <div className="aspect-video bg-gradient-to-br from-zinc-800 to-black border border-gray-700 rounded-sm mb-6 flex items-center justify-center relative overflow-hidden">
                <div className="absolute inset-0 bg-noise opacity-30"></div>
                <div className="relative z-10 text-center">
                  <img 
                    src={currentFeature.image} 
                    alt={currentFeature.title}
                    className="w-16 h-16 mx-auto mb-4 opacity-60"
                  />
                  <p className="text-green-400 font-mono text-sm">
                    [PROTOCOL_{currentFeature.title.toUpperCase().replace(/\s/g, '_')}_ACTIVE]
                  </p>
                </div>
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent"></div>
                <div className="scanline opacity-20"></div>
              </div>
              
              <h3 className="text-xl font-bold text-green-400 mb-4">
                {currentFeature.title}
              </h3>
              
              <div className="space-y-3">
                {currentFeature.details.map((detail, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <span className="text-green-500 mt-1 text-xs">◈</span>
                    <p className="text-gray-300 text-sm analog-text break-words">
                      {detail.split('██████').map((part, i) => 
                        i > 0 ? (
                          <span key={i}>
                            <span className="bg-red-500 text-black px-1">██████</span>
                            {part}
                          </span>
                        ) : part
                      )}
                    </p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
        
        <div className="text-center mt-12">
          <Button 
            variant="outline" 
            className="pulse-button bg-black border border-green-500 text-green-500 py-6 px-8 text-lg hover:bg-green-500/10"
            onClick={() => navigate('/auth')}
          >
            Initialize Neural Interface
          </Button>
        </div>
      </div>
    </section>
  );
};

export default FeaturesDemoSection;
